import { useEffect, useRef, useState } from 'react';

/**
 * Audible alarm for safety violations.
 *
 * A toggle the operator arms (browsers only allow sound after a user gesture, so
 * the AudioContext is created on the click). While armed it polls the events
 * feed; when a new critical/warning event appears — a person in a no-entry zone,
 * a vehicle where none is allowed — it sounds a two-tone siren and flashes the
 * detail. The events come from the same evaluateSafety brain the RTSP monitor
 * runs, so this fires on the real 24/7 feed, not just the tab in front of you.
 *
 * A physical alarm lamp/bell on site is the next step: point ALARM_WEBHOOK at a
 * relay and the same trigger can pulse it.
 */
type Ev = { id: string; severity: string; detail: string; kind: string };

export function SafetyAlarm() {
  const [on, setOn] = useState(() => {
    try {
      return localStorage.getItem('safety.alarm') === 'on';
    } catch {
      return false;
    }
  });
  const [latest, setLatest] = useState<Ev | null>(null);
  const ac = useRef<AudioContext | null>(null);
  const lastId = useRef<string | null>(null);
  const armed = useRef(false);

  const siren = (severity: string) => {
    const ctx = ac.current;
    if (!ctx) return;
    const t0 = ctx.currentTime;
    const cycles = severity === 'critical' ? 5 : 2;
    for (let i = 0; i < cycles; i++) {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'square';
      o.frequency.setValueAtTime(i % 2 ? 660 : 990, t0 + i * 0.34);
      g.gain.setValueAtTime(0.0001, t0 + i * 0.34);
      g.gain.exponentialRampToValueAtTime(0.22, t0 + i * 0.34 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + i * 0.34 + 0.31);
      o.connect(g);
      g.connect(ctx.destination);
      o.start(t0 + i * 0.34);
      o.stop(t0 + i * 0.34 + 0.33);
    }
  };

  useEffect(() => {
    try {
      localStorage.setItem('safety.alarm', on ? 'on' : 'off');
    } catch {
      /* private browsing */
    }
    if (!on) {
      setLatest(null);
      return;
    }
    let stop = false;
    armed.current = false; // first poll only sets the baseline — no alarm for history
    const poll = async () => {
      try {
        const r = await fetch('/api/safety/events?limit=8');
        if (r.ok) {
          const evs = (await r.json()) as Ev[];
          const newest = evs[0];
          if (newest) {
            if (!armed.current) {
              lastId.current = newest.id;
              armed.current = true;
            } else if (newest.id !== lastId.current) {
              const idx = evs.findIndex((e) => e.id === lastId.current);
              const fresh = idx === -1 ? evs : evs.slice(0, idx);
              lastId.current = newest.id;
              const hit = fresh.find((e) => e.severity === 'critical') ?? fresh.find((e) => e.severity === 'warning');
              if (hit) {
                setLatest(hit);
                siren(hit.severity);
              }
            }
          }
        }
      } catch {
        /* keep trying */
      }
      if (!stop) setTimeout(poll, 3000);
    };
    void poll();
    return () => {
      stop = true;
    };
  }, [on]);

  const toggle = () => {
    if (!on) {
      try {
        const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        ac.current = ac.current ?? new Ctor();
        void ac.current.resume?.();
      } catch {
        /* no audio available */
      }
    }
    setOn((v) => !v);
  };

  return (
    <>
      <button
        className={`btn sm${on ? ' primary' : ''}`}
        onClick={toggle}
        title="Play a siren when a new safety violation is raised on any camera"
      >
        {on ? 'Alarm armed' : 'Arm alarm'}
      </button>
      {on && latest && (
        <div
          className="panel"
          style={{ borderColor: latest.severity === 'critical' ? 'var(--bad)' : 'var(--warn)', animation: 'pulse 1s 4' }}
          onClick={() => setLatest(null)}
        >
          <strong style={{ color: latest.severity === 'critical' ? 'var(--bad)' : 'var(--warn)' }}>
            ⚠ {latest.severity === 'critical' ? 'ALARM' : 'Warning'}
          </strong>{' '}
          {latest.detail} <span className="muted" style={{ fontSize: 12 }}>· tap to dismiss</span>
        </div>
      )}
    </>
  );
}
